import React, { useState } from 'react'
import { OmButton, OmAlert, OmTag, OmIcon, useMessage } from '@owma/kit'

import styles from './EMLPreview.module.scss'

interface EmailAddress {
  email: string
  name?: string
}

interface EmailData {
  from?: EmailAddress[]
  to?: EmailAddress[]
  cc?: EmailAddress[]
  bcc?: EmailAddress[]
  subject?: string
  attachments?: any[]
}

interface ValidationResult {
  errors: string[]
  warnings: string[]
  isValid: boolean
}

interface EMLPreviewProps {
  emlContent: string
  emailData?: EmailData
  validationResult?: ValidationResult
  onClose?: () => void
}

const EMLPreview: React.FC<EMLPreviewProps> = ({ emlContent, emailData, validationResult }) => {
  const [copied, setCopied] = useState<boolean>(false)
  const message = useMessage()

  const handleCopyToClipboard = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(emlContent)
      setCopied(true)
      message.success('EML content copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      message.error('Failed to copy to clipboard')
    }
  }

  const handleDownload = (): void => {
    const blob = new Blob([emlContent], { type: 'message/rfc822' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `email_preview_${new Date().getTime()}.eml`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    message.success('EML file downloaded')
  }

  const renderEmailSummary = (): React.ReactElement | null => {
    if (!emailData) return null

    return (
      <div className={styles.emailSummary}>
        <h4>Email Summary</h4>
        <div className={styles.summaryItem}>
          <strong>From:</strong> {emailData.from?.[0]?.email || 'N/A'}
        </div>
        <div className={styles.summaryItem}>
          <strong>To:</strong> {emailData.to?.map((addr) => addr.email).join(', ') || 'N/A'}
        </div>
        {emailData.cc?.length > 0 && (
          <div className={styles.summaryItem}>
            <strong>CC:</strong> {emailData.cc.map((addr) => addr.email).join(', ')}
          </div>
        )}
        {emailData.bcc?.length > 0 && (
          <div className={styles.summaryItem}>
            <strong>BCC:</strong> {emailData.bcc.map((addr) => addr.email).join(', ')}
          </div>
        )}
        <div className={styles.summaryItem}>
          <strong>Subject:</strong> {emailData.subject || 'No subject'}
        </div>
        <div className={styles.summaryItem}>
          <strong>Attachments:</strong> {emailData.attachments?.length || 0} files
        </div>
        <div className={styles.summaryItem}>
          <strong>EML Size:</strong> {new Blob([emlContent || '']).size} bytes
        </div>
        <div className={styles.summaryItem}>
          <strong>Generated at:</strong> {new Date().toLocaleString()}
        </div>
      </div>
    )
  }

  const renderValidationResult = (): React.ReactElement | null => {
    if (!validationResult) return null

    const { errors, warnings, isValid } = validationResult

    return (
      <div className={styles.validationResult}>
        <h4>EML Validation</h4>

        {isValid ? (
          <OmTag icon={<OmIcon name="checkCircle" />} color="success" className={styles.validTag}>
            EML is valid
          </OmTag>
        ) : (
          <OmTag
            icon={<OmIcon name="exclamationCircle" />}
            color="error"
            className={styles.invalidTag}
          >
            EML has errors
          </OmTag>
        )}

        {errors.length > 0 && (
          <OmAlert
            message="Errors"
            description={
              <ul className={styles.errorList}>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            }
            type="error"
            showIcon
            className={styles.alertMargin}
          />
        )}

        {warnings.length > 0 && (
          <OmAlert
            message="Warnings"
            description={
              <ul className={styles.warningList}>
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            }
            type="warning"
            showIcon
            className={styles.alertMargin}
          />
        )}
      </div>
    )
  }

  return (
    <div className={styles.emlPreview}>
      {renderEmailSummary()}

      {renderValidationResult()}

      <div className={styles.actions}>
        <OmButton
          icon={<OmIcon name="copy" />}
          onClick={handleCopyToClipboard}
          className={copied ? styles.copied : ''}
        >
          {copied ? 'Copied!' : 'Copy EML'}
        </OmButton>
        <OmButton icon={<OmIcon name="download" />} onClick={handleDownload}>
          Download EML
        </OmButton>
      </div>

      <div className={styles.emlContent}>
        <h4>EML Content Preview</h4>
        <pre className={styles.emlText}>{emlContent}</pre>
      </div>
    </div>
  )
}

export default EMLPreview
